/**
 *  @file src/utils/googleAnalytics.js
 *  @description Support Google analytics.
 * 
 *  FFT Demo Event Definitions
 * 
 *  Category        Action                              Label
 *  ------------------------------------------------------------------------
 * 
 *  demo            show entities click                 
 *  demo            show sentiment click               
 *  knowledgebase   search knowledgebase click          <search string>           
 *
 */
import ReactGA from 'react-ga';
import ReactGA4 from 'react-ga4';

const GA_TRACKING_SESSION_ID = 'ga_fft_trackingId';

export const categories = {
    DEMO: 'demo',
    KB: 'knowledgebase'
};

export const actions = {
    ENTITIES_CLICK: 'show entities click',
    SENTIMENT_CLICK: 'show sentiment click',
    KB_SEARCH_CLICK: 'search knowledgebase click'
};

export default class GoogleAnalytics {

    /**
     *  tracking id - GA tracking id in UA-* or G-* format.
     */
    set trackingId(val) { this._trackingId = val; sessionStorage.setItem(GA_TRACKING_SESSION_ID, val); this.__init(); }
    get trackingId() { return this._trackingId; }

    /**
     *  @constructor
     *  @param {string} trackingId - Tracking Id, either in UA-* or G-* format.
     */
    constructor(trackingId=undefined) {
        this._trackingId = null;
        if (trackingId) {
            this._trackingId = trackingId;
            sessionStorage.setItem(GA_TRACKING_SESSION_ID, trackingId);
            this.__init();
        } else if (sessionStorage.getItem(GA_TRACKING_SESSION_ID)) {
            this._trackingId = sessionStorage.getItem(GA_TRACKING_SESSION_ID);
        }
    }

    /**
     * @function __init
     * @description Initialize the GA service with the internal tracking Id.
     */
    __init() {
        if (this._trackingId) {
            if (this._trackingId.toLowerCase().startsWith('ua-')) {
                try {
                    ReactGA.initialize(this._trackingId);
                    console.log('initializedGA');
                } catch(e) {
                    console.log('ERROR initializedGA: ', e);
                }
                try {
                    ReactGA.pageview(window.location.pathname + window.location.search);
                    console.log('pageviewGA: ', window.location.pathname + window.location.search);
                } catch(e) {
                    console.log('ERROR pageviewGA: ', e);
                }
            } else if (this._trackingId.toLowerCase().startsWith('g-')) {
                try {
                    ReactGA4.initialize(this._trackingId);
                    console.log('initializedGA4');
                } catch(e) {
                    console.log('ERROR: initializedGA4: ', e);
                }
                try {
                    ReactGA4.send({hitType: 'pageview', page: window.location.pathname + window.location.search });
                    console.log('pageviewGA4: ', window.location.pathname + window.location.search);
                } catch(e) {
                    console.log('ERROR: pageviewGA4: ', e);
                }
            }
        }
    }

    /**
     * @function pageview 
     * @description Sends a pageview event to the google analytics service
     * @param {*} location 
     * @returns 
     */
    pageview(location) {
        if (location && this._trackingId) {
            if (this._trackingId.toLowerCase().startsWith('ua-')) {     
                try {   
                    ReactGA.pageview(location.pathname + location.search);
                    console.log('pageviewGA: ', location.pathname + location.search)
                } catch(e) {
                    console.log('ERROR: pageviewGA: ', e);
                }
            } else if (this._trackingId.toLowerCase().startsWith('g-')) {    
                try {    
                    ReactGA4.send({hitType: 'pageview', page: location.pathname + location.search });
                    console.log('pageviewGA4: ', location.pathname + location.search);
                } catch(e) {
                    console.log('ERROR: pageviewGA4: ', e);
                }
            }
        }
    }

    /**
     * @function event
     * @description Sends an event to the google analytics service
     * @param {string} category - Broad category
     * @param {string} action - user interface action
     * @param {string} label - Extra data to further define the action
     */
    event(category, action, label='') {
        if (category && action && this._trackingId) {
            if (this._trackingId.toLowerCase().startsWith('ua-')) {     
                try {   
                    ReactGA.event({category, action, label});
                    console.log('eventGA: ', category, action, label);
                } catch(e) {
                    console.log('ERROR: eventGA: ', e);
                }
            } else if (this._trackingId.toLowerCase().startsWith('g-')) {    
                try {    
                    ReactGA4.event({category, action, label});
                    console.log('eventGA4: ', category, action, label);
                } catch(e) {
                    console.log('ERROR: eventGA4: ', e);
                }
            }
        }       
    }

    /**
     * @function end
     * @description Called to end a google analytics session (usually on user logout)
     */
    end() {
        if (this._trackingId) {
            this._trackingId = null;
            sessionStorage.removeItem(GA_TRACKING_SESSION_ID);
        }
    }
}
